import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Alert,
  Spinner,
  Badge,
} from "reactstrap";
import logo from "../../assets/img/icon-transparent.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { generateCheckoutID } from "actions/quotationReview";
import axios from "axios";
import { WORK_ORDER } from "components/Common/constants";

// Custom RightPanelCard component - Now using CSS classes
const RightPanelCard = ({
  isOpen,
  onClose,
  title,
  children,
  width = "500px",
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="right-panel-backdrop" />
      <div 
        className={`right-panel ${isOpen ? 'open' : ''}`}
        style={{ width }}
      >
        <div className="right-panel-header">
          <h3 className="right-panel-title">{title}</h3>
          <button onClick={onClose} className="right-panel-close">
            ×
          </button>
        </div>
        <div className="right-panel-body">{children}</div>
      </div>
    </>
  );
};

const PaymentConfirmScreen = ({ paymentDetails, onConfirm, onCancel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  let quotationData = state?.quotationData;
  let uuid = state?.uuid;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCheckoutPanel, setShowCheckoutPanel] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Sample payment data - replace with your actual props
  const paymentData = paymentDetails || {
    amount: 1595.05,
    currency: "SAR",
    quotationId: "QTN-2024-001",
    customerName: "John Doe",
    customerEmail: "john.doe@example.com",
    paymentMethod: "Credit Card / MADA",
    dueDate: "2024-12-31",
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: quotationData?.currency || "SAR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const showAlertMessage = (message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 5000);
  };

  const handleConfirmPayment = () => {
    setShowCheckoutPanel(true);
  };

  const handleMakePayment = () => {
    setShowConfirmModal(false);
    setShowCheckoutPanel(true);
  };

  const handleCheckoutComplete = (paymentResult) => {
    setShowCheckoutPanel(false);
    setProcessing(true);

    setTimeout(() => {
      setPaymentStatus("success");
      showAlertMessage("Payment processed successfully!", "success");

      if (onConfirm) {
        onConfirm({ success: true, transactionId: "TXN_" + Date.now() });
      }
      setProcessing(false);
    }, 1000);
  };

  const handleCheckoutError = (error) => {
    setShowCheckoutPanel(false);
    setPaymentStatus("error");
    showAlertMessage(
      error?.message || "Payment failed. Please try again.",
      "danger",
    );

    if (onConfirm) {
      onConfirm({ success: false, error: error?.message });
    }
  };

  const cancelPayment = () => {
    setShowConfirmModal(false);
    setTimeout(() => {
      navigate(`/review-quotation/${uuid}`);
    }, 1500);
  };

  const CheckoutContent = ({
    onSuccess,
    onError,
    quotationData: quoteData,
  }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [checkoutId, setCheckoutId] = useState(null);
    const [widgetURL, setWidgetURL] = useState("");
    const [showPaymentIframe, setShowPaymentIframe] = useState(false);
    const [iframeLoading, setIframeLoading] = useState(true);
    const iframeRef = React.useRef(null);

    const getCheckoutId = async () => {
  try {
    // ✅ Stash the quotation uuid so it survives the HyperPay round trip.
    //    PaymentStatus will read this on return.
    if (uuid) {
      sessionStorage.setItem("pendingQuotationUuid", uuid);
    }

    let payload = {
      uuid: uuid,
      quotationID: quotationData?.ID,
      payableAmount: quotationData?.payableAmount,
      returnUrl: `${window.location.origin}${window.location.pathname}`,
    };
    const res = await generateCheckoutID(payload);
    if (res?.code === 200) {
      return {
        checkoutID: res?.data?.checkoutID,
        widgetURL: res?.data?.widgetURL,
      };
    }
  } catch (error) {
    console.error("Error getting checkout ID:", error);
    throw error;
  }
};

    const createIframeContent = (checkoutId, widgetURL) => {
  return `
  <!DOCTYPE html>
  <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          background: #ffffff;
          padding: 20px;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
        }

        /* Fix for input fields on mobile */
        input, select, textarea {
          font-size: 16px !important; /* Prevents iOS zoom on focus */
          -webkit-appearance: none;
          appearance: none;
          touch-action: manipulation;
        }

        /* Fix for iframe scrolling on mobile */
        .payment-container {
          max-width: 500px;
          margin: 0 auto;
          -webkit-overflow-scrolling: touch;
        }

        .payment-widget-wrapper {
          margin-top: 20px;
          touch-action: manipulation;
        }

        /* Fix for payment widget inputs */
        .paymentWidgets input,
        .paymentWidgets .input-field {
          font-size: 16px !important;
          min-height: 44px !important; /* Better touch targets */
          touch-action: manipulation !important;
        }

        .payment-header h3 {
          color: #1f2937;
          font-size: 20px;
          margin-bottom: 8px;
        }

        .payment-header p {
          color: #6b7280;
          font-size: 14px;
        }

        .payment-summary {
          background: #f9fafb;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 24px;
          border: 1px solid #e5e7eb;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .summary-row:last-child {
          margin-bottom: 0;
          padding-top: 8px;
          border-top: 1px solid #e5e7eb;
        }

        .summary-label {
          color: #6b7280;
          font-size: 14px;
        }

        .summary-value {
          color: #1f2937;
          font-weight: 500;
          font-size: 14px;
        }

        .total-amount {
          font-size: 18px;
          font-weight: bold;
          color: #2563eb;
        }

        .footer-note {
          text-align: center;
          margin-top: 20px;
          font-size: 11px;
          color: #9ca3af;
        }

        /* Mobile responsive fixes */
        @media (max-width: 480px) {
          body {
            padding: 12px;
          }
          .payment-container {
            max-width: 100%;
          }
          .payment-header h3 {
            font-size: 18px;
          }
          .payment-summary {
            padding: 12px;
          }
          .summary-label,
          .summary-value {
            font-size: 13px;
          }
          .total-amount {
            font-size: 16px;
          }
        }
      </style>
      <script src="${widgetURL}"></script>
    </head>
    <body>
      <div class="payment-container">
        <div class="payment-header">
          <h3>Secure Payment</h3>
          <p>Enter your card details to complete payment</p>
        </div>

        <div class="payment-summary">
          <div class="summary-row">
            <span class="summary-label">Amount to Pay</span>
            <span class="summary-value total-amount">${quoteData?.currency || "SAR"} ${parseFloat(quoteData?.payableAmount || 0).toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Merchant</span>
            <span class="summary-value">${quoteData?.merchantName || "Falcon Kitchens"}</span>
          </div>
        </div>

        <div class="payment-widget-wrapper">
          <form class="paymentWidgets" data-brands="MADA VISA MASTER AMEX"></form>
        </div>

        <div class="footer-note">
          Payment secured by HyperPay
        </div>
      </div>

      <script>
        // Enable touch events
        document.addEventListener('touchstart', function() {}, { passive: true });
        
        // Fix for input fields on mobile
        document.addEventListener('focusin', function(e) {
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') {
            e.target.style.fontSize = '16px';
          }
        });

        window.wpwlOptions = {
          style: "plain",
          locale: "en",
          maskCvv: true,
          onReady: function() {
            window.parent.postMessage({
              type: 'widget-ready',
              checkoutId: '${checkoutId}'
            }, '*');
          },
          onSuccess: function(result) {
            window.parent.postMessage({
              type: 'payment-success',
              result: result,
              transactionId: result.id,
              amount: '${quoteData?.payableAmount}',
              currency: '${quoteData?.currency}'
            }, '*');
          },
          onError: function(error) {
            window.parent.postMessage({
              type: 'payment-error',
              error: error,
              message: error.message || 'Payment processing failed'
            }, '*');
          },
          onCancel: function() {
            window.parent.postMessage({
              type: 'payment-cancel'
            }, '*');
          }
        };
      </script>
    </body>
  </html>
`;
};

    React.useEffect(() => {
      const handleMessage = (event) => {
        const allowedOrigins = [
          "https://eu-test.oppwa.com",
          "https://oppwa.com",
          window.location.origin,
        ];

        if (!allowedOrigins.includes(event.origin)) {
          return;
        }

        if (event.data && event.data.type) {
          switch (event.data.type) {
            case "widget-ready":
              setIframeLoading(false);
              break;

            case "payment-success":
              onSuccess({
                success: true,
                transactionId:
                  event.data.transactionId ||
                  event.data.result?.id ||
                  "TXN_" + Date.now(),
                amount: quoteData?.payableAmount,
                currency: quoteData?.currency,
              });
              setShowPaymentIframe(false);
              break;

            case "payment-error":
              onError({
                message:
                  event.data.message || "Payment failed. Please try again.",
              });
              setShowPaymentIframe(false);
              break;

            case "payment-cancel":
              onError({ message: "Payment cancelled by user" });
              setShowPaymentIframe(false);
              break;

            default:
              break;
          }
        }
      };

      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }, [onSuccess, onError, quoteData]);

    const initializePayment = async () => {
      setIsLoading(true);
      try {
        const response = await getCheckoutId();
        if (response) {
          setCheckoutId(response?.checkoutID);
          setWidgetURL(response?.widgetURL);
          setShowPaymentIframe(true);
          setIframeLoading(true);
        } else {
          throw new Error("Failed to get checkout ID");
        }
      } catch (error) {
        console.error("Payment initialization error:", error);
        onError({ message: "Failed to initialize payment. Please try again." });
      } finally {
        setIsLoading(false);
      }
    };

    const closeIframe = () => {
      setShowPaymentIframe(false);
      setCheckoutId(null);
      setIframeLoading(true);
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: quoteData?.currency || "SAR",
        minimumFractionDigits: 2,
      }).format(amount);
    };

    if (isLoading) {
      return (
        <div className="text-center" style={{ padding: "40px 20px" }}>
          <div className="spinner" style={{ margin: "0 auto" }} />
          <p style={{ marginTop: "20px", color: "#666" }}>
            Initializing secure payment gateway...
          </p>
        </div>
      );
    }

    if (showPaymentIframe && checkoutId && widgetURL) {
      const iframeContent = createIframeContent(checkoutId, widgetURL);

      return (
        <div className="checkout-container">
          <div className="iframe-header">
            <h3 className="iframe-title"></h3>
            <button onClick={closeIframe} className="iframe-close-btn">
              ×
            </button>
          </div>

          <div className="iframe-wrapper">
            {iframeLoading && (
              <div className="iframe-loading">
                <div className="spinner" />
                <p className="loading-text">Loading secure payment form...</p>
              </div>
            )}

            <iframe
              ref={iframeRef}
              srcDoc={iframeContent}
              title="Secure Payment Gateway"
              className="iframe-element"
              onLoad={() => {
                setTimeout(() => {
                  setIframeLoading(false);
                }, 1000);
              }}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-top-navigation allow-pointer-lock"
              allow="payment *; clipboard-read; clipboard-write"
              scrolling="yes"
              style={{
                WebkitOverflowScrolling: 'touch',
                touchAction: 'auto',
              }}
            />
          </div>

          <div className="secure-footer">
            <span>🔒 Payment secured by HyperPay | 3D Secure Enabled</span>
          </div>
        </div>
      );
    }

    return (
      <div className="checkout-container">
        <div className="checkout-header">
          <h3 className="checkout-title">Checkout</h3>
          <p className="checkout-subtitle">
            Please review your payment details below
          </p>
        </div>

        <div className="checkout-summary">
          <h4 className="checkout-summary-title">Payment Summary</h4>

          <div className="checkout-row">
            <span className="checkout-label">Quotation ID:</span>
            <span className="checkout-value">{quoteData?.ID}</span>
          </div>

          <div className="checkout-row">
            <span className="checkout-label">{WORK_ORDER} NO:</span>
            <span className="checkout-value">{quoteData?.jobID}</span>
          </div>

          <div className="checkout-row checkout-total">
            <span className="checkout-total-label">Total Amount:</span>
            <span className="checkout-total-value">
              {formatCurrency(quoteData?.payableAmount)}
            </span>
          </div>
        </div>

        <div className="checkout-actions">
          <button
            onClick={() => onError({ message: "Payment cancelled" })}
            className="checkout-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={initializePayment}
            className="checkout-proceed-btn"
          >
            Proceed to Secure Payment
          </button>
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="payment-confirm-screen">
      <Col lg={6} md={8} sm={10} xs={12}>
        <Card className="payment-card shadow border-0">
          <CardBody className="p-0">
            <div className="payment-header text-center">
              <div className="logo-wrapper">
                <img src={logo} alt="Company Logo" className="payment-logo" />
              </div>
              <h2 className="payment-title mb-0">Payment Confirmation</h2>
              {paymentStatus && (
                <Badge
                  color={paymentStatus === "success" ? "success" : "danger"}
                  pill
                  className="mt-3 px-3 py-2"
                >
                  {paymentStatus === "success"
                    ? "PAYMENT SUCCESSFUL"
                    : "PAYMENT FAILED"}
                </Badge>
              )}
            </div>

            {showAlert && (
              <div className="alert-container">
                <Alert
                  color={alertType}
                  className="mb-0"
                  toggle={() => setShowAlert(false)}
                >
                  {alertMessage}
                </Alert>
              </div>
            )}

            <div className="payment-details">
              <div className="amount-section text-center mb-3 pb-2 border-bottom">
                <p className="amount-label mb-1">Total Amount to Pay</p>
                <h1 className="amount-value fw-bold mb-0">
                  {formatCurrency(quotationData?.payableAmount)}
                </h1>
              </div>

              <div className="details-section">
                <h5 className="section-title mb-2">Payment Summary</h5>

                <div className="detail-row d-flex justify-content-between border-bottom">
                  <span className="detail-label">Quotation ID:</span>
                  <span className="detail-value">{quotationData?.ID}</span>
                </div>
                <div className="detail-row d-flex justify-content-between border-bottom">
                  <span className="detail-label">{WORK_ORDER} NO:</span>
                  <span className="detail-value">{quotationData?.jobID}</span>
                </div>
              </div>

              <div className="total-section mt-3 pt-2 border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="total-label">TOTAL PAYABLE:</span>
                  <span className="total-value">
                    {formatCurrency(quotationData?.payableAmount)}
                  </span>
                </div>
              </div>
            </div>

            {!paymentStatus && (
              <div className="payment-actions">
                <Button
                  block
                  onClick={handleConfirmPayment}
                  disabled={processing}
                  className="pay-now-btn fw-bold"
                >
                  {processing ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    "Confirm & Pay"
                  )}
                </Button>

                <Button
                  color="link"
                  block
                  onClick={cancelPayment}
                  disabled={processing}
                  className="cancel-btn mt-2"
                >
                  Cancel Payment
                </Button>
              </div>
            )}

            {paymentStatus === "success" && (
              <div className="payment-actions">
                <Button
                  color="success"
                  block
                  onClick={() => (window.location.href = "/dashboard")}
                  className="py-2 fw-bold"
                  style={{ borderRadius: "0.5rem" }}
                >
                  Go to Dashboard
                </Button>
              </div>
            )}

            {paymentStatus === "error" && (
              <div className="payment-actions">
                <Button
                  block
                  onClick={() => window.location.reload()}
                  className="py-2 fw-bold"
                  style={{
                    backgroundColor: "#2a5b86",
                    borderColor: "#2a5b86",
                    borderRadius: "0.5rem",
                  }}
                >
                  Try Again
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>

      <Modal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(false)}
        className="confirm-modal"
      >
        <ModalBody>
          <div className="text-center">
            <div className="mb-2">
              <h4 className="fw-bold modal-amount">
                {formatCurrency(paymentData.amount)}
              </h4>
            </div>
            <p className="mb-0">
              Are you sure you want to proceed with this payment?
            </p>
            <small className="text-muted">This action cannot be undone.</small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowConfirmModal(false)}
            className="modal-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            color="success"
            onClick={handleMakePayment}
            className="modal-pay-btn"
          >
            Make Payment
          </Button>
        </ModalFooter>
      </Modal>

      <RightPanelCard
        isOpen={showCheckoutPanel}
        onClose={() => setShowCheckoutPanel(false)}
        width="625px"
      >
        <CheckoutContent
          onSuccess={handleCheckoutComplete}
          onError={handleCheckoutError}
          quotationData={quotationData}
        />
      </RightPanelCard>
    </Container>
  );
};

export default PaymentConfirmScreen;